import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { toggleHasManyValue } from 'uplisting-frontend/utils/toggle-value';
import PropertyModel from 'uplisting-frontend/models/property';

type BatteryStatus = 'critical' | 'low' | 'good' | 'full';

function getBatteryStatus(level: number): BatteryStatus {
  if (level >= 0.8) {
    return 'full';
  } else if (level >= 0.4 && level < 0.8) {
    return 'good';
  } else if (level >= 0.2 && level < 0.4) {
    return 'low';
  }

  return 'critical';
}

export default class SeamLockableModel extends Model {
  @attr('string') deviceId!: string;
  @attr('string') deviceType!: string;
  @attr('string') name!: string;

  @attr('number') batteryLevel!: number;
  @attr('number') keypadBatteryLevel!: number | null;
  @attr('boolean') doorOpen!: boolean;
  @attr('boolean') locked!: boolean;
  @attr('boolean') online!: boolean;

  @hasMany('property', { async: false, inverse: null })
  properties!: ModelRegistry['property'][];

  @hasMany('multi-unit', { async: false, inverse: null })
  multiUnits!: ModelRegistry['multi-unit'][];

  @belongsTo('seam-connection', { async: false, inverse: null })
  seamConnection!: ModelRegistry['seam-connection'];

  @cached
  get batteryStatus(): BatteryStatus {
    return getBatteryStatus(this.batteryLevel);
  }

  @cached
  get keypadBatteryStatus(): BatteryStatus {
    return getBatteryStatus(this.keypadBatteryLevel as number);
  }

  public toggleOption(
    option: ModelRegistry['property'] | ModelRegistry['multi-unit'],
  ): void {
    if (option instanceof PropertyModel) {
      toggleHasManyValue(this, 'properties', option);

      option.multiUnits.forEach((multiUnit) => {
        if (this.multiUnits.includes(multiUnit)) {
          this.multiUnits.splice(this.multiUnits.indexOf(multiUnit), 1);
        }
      });
    } else {
      toggleHasManyValue(this, 'multiUnits', option);

      if (this.properties.includes(option.property)) {
        this.properties.splice(this.properties.indexOf(option.property), 1);
      }
    }
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'seam-lockable': SeamLockableModel;
  }
}
